import type en from '../en/admin'
import type { Translation } from '../types'

/** Platform administration */
const admin: Translation<typeof en> = {
  "admin.module.webhooks.label": "Webhooks",
  "admin.module.webhooks.desc": "Webhooks HTTP sortants sur événement",
  "admin.module.branding.label": "Identité visuelle",
  "admin.module.branding.desc": "Logo et couleurs propres, marque blanche",
  "admin.module.apiKeys.label": "Clés API",
  "admin.module.apiKeys.desc": "Accès à l’API pour des scripts et d’autres systèmes",
  "admin.module.templates.label": "Modèles",
  "admin.module.templates.desc": "Modèles et assistant de configuration réseau",
  "admin.module.customDomain.label": "Domaine propre",
  "admin.module.customDomain.desc": "Connexion sous l’adresse propre de l’entreprise",
  "admin.module.customEmail.label": "Serveur de messagerie propre",
  "admin.module.customEmail.desc": "SMTP par entreprise",
  "admin.module.sso.label": "Authentification unique",
  "admin.module.sso.desc": "Connexion via le fournisseur d’identité de l’entreprise (OpenID Connect)",
  "admin.module.ownWhatsapp.label": "WhatsApp propre",
  "admin.module.ownWhatsapp.desc": "Le compte Meta ou Twilio propre à l’entreprise",
  "admin.module.ownRcs.label": "RCS propre",
  "admin.module.ownRcs.desc": "L’expéditeur RCS propre à l’entreprise (Google ou Twilio)",
  "admin.module.linkedWhatsapp.label": "Numéro WhatsApp associé",
  "admin.module.linkedWhatsapp.desc": "L’entreprise associe son propre numéro WhatsApp au serveur (open-wa)",
  "admin.module.unifi.label": "UniFi",
  "admin.module.unifi.desc": "Contrôleurs UniFi et Site Manager",
  "admin.module.teltonika.label": "Teltonika",
  "admin.module.teltonika.desc": "Appareils RutOS et RMS",
  "admin.module.peplink.label": "Peplink",
  "admin.module.peplink.desc": "Appareils Peplink et InControl2",
  "admin.module.mikrotik.label": "MikroTik",
  "admin.module.mikrotik.desc": "Routeurs RouterOS, points d’accès et liaisons radio",
  "admin.module.velanox.label": "Velanox One Speed",
  "admin.module.velanox.desc": "Boîtiers et points de terminaison Velanox",
  "admin.overview.title": "Vue d’ensemble de la plateforme",
  "admin.overview.subtitle": "Tableau de bord de l’administration de la plateforme",
  "admin.overview.companies": "Entreprises",
  "admin.overview.users": "Utilisateurs",
  "admin.overview.sites": "Sites",
  "admin.overview.pendingJobs": "Tâches en attente",
  "admin.overview.revocations": "Révocations de certificats en cours de nouvel essai",
  "admin.overview.revocationsText": "Ces certificats VPN n’ont pas encore pu être révoqués et fonctionnent donc toujours. Chacun est réessayé (au plus une heure d’intervalle) jusqu’à ce que cela réussisse.",
  "admin.overview.siteCert": "Certificat de site",
  "admin.overview.userCert": "Certificat d’utilisateur",
  "admin.overview.tries": { one: "{count} essai · prochain essai {when}", other: "{count} essais · prochain essai {when}" },
  "admin.overview.byStatus": "Entreprises par statut",
  "admin.overview.jobs": "File des tâches",
  "admin.overview.all": "Toutes",
  "admin.overview.refreshJobs": "Actualiser les tâches",
  "admin.overview.noJobs": "Aucune tâche",
  "admin.overview.audit": "Journal d’audit récent",
  "admin.overview.noAudit": "Aucune entrée dans le journal d’audit",
  "admin.overview.system": "Système",
  "admin.vpn.title": "Gestion du VPN",
  "admin.vpn.subtitle": "Serveurs OpenVPN, réseaux et comptes de chaque entreprise",
  "admin.vpn.refresh": "Actualiser",
  "admin.vpn.server": "Serveur",
  "admin.vpn.running": "En marche",
  "admin.vpn.stopped": "Arrêté",
  "admin.vpn.since": "Depuis {when}",
  "admin.vpn.connectedNow": "Connectés maintenant",
  "admin.vpn.ofActive": { one: "sur {count} compte actif", other: "sur {count} comptes actifs" },
  "admin.vpn.totalAccounts": "Comptes au total",
  "admin.vpn.acrossCompanies": { one: "dans {count} entreprise", other: "dans {count} entreprises" },
  "admin.vpn.networks": "Réseaux",
  "admin.vpn.isolated": "sous-réseaux /24 séparés",
  "admin.vpn.currentlyConnected": "Connectés maintenant ({count})",
  "admin.vpn.commonName": "Nom commun",
  "admin.vpn.realIp": "IP réelle",
  "admin.vpn.sinceCol": "Depuis",
  "admin.vpn.usersSites": "Utilisateurs : {users} · Sites : {sites}",
  "admin.vpn.connectedOf": { one: "{connected} connecté(s) / {count} compte", other: "{connected} connecté(s) / {count} comptes" },
  "admin.vpn.noAccounts": "Aucun compte VPN pour l’instant",
  "admin.vpn.name": "Nom",
  "admin.vpn.logs": "Journaux OpenVPN",
  "admin.vpn.logLines": "Lignes du journal",
  "admin.vpn.lines": { one: "{count} ligne", other: "{count} lignes" },
  "admin.vpn.refreshLogs": "Actualiser les journaux",
  "admin.vpn.hide": "Masquer",
  "admin.vpn.showLogs": "Afficher les journaux",
  "admin.vpn.loadHint": "Choisissez « Afficher les journaux » pour les charger",
  "admin.plans.toggleFailed": "Non modifié",
  "admin.plans.deleted": "Formule supprimée",
  "admin.plans.deleteFailed": "La formule ne peut pas être supprimée — elle a peut-être des licences actives",
  "admin.plans.title": "Formules de licence",
  "admin.plans.subtitle": "Types de licence réutilisables et les modules qu’ils débloquent",
  "admin.plans.new": "Nouvelle formule",
  "admin.plans.deleteConfirm": "Supprimer la formule « {name} » ? Les entreprises qui l’utilisent gardent leur licence actuelle.",
  "admin.plans.empty": "Aucune formule de licence pour l’instant — créez-en une pour l’attribuer aux entreprises",
  "admin.plans.createTitle": "Créer une formule de licence",
  "admin.plans.created": "Formule créée",
  "admin.plans.editTitle": "Modifier : {name}",
  "admin.plans.updated": "Formule mise à jour",
  "admin.plans.public": "Publique",
  "admin.plans.activeCount": { one: "{count} active", other: "{count} actives" },
  "admin.plans.editNamed": "Modifier {name}",
  "admin.plans.deleteNamed": "Supprimer {name}",
  "admin.plans.sites": "Sites",
  "admin.plans.users": "Utilisateurs",
  "admin.plans.duration": "Durée",
  "admin.plans.daysShort": "{count} j",
  "admin.plans.modules": "Modules",
  "admin.plans.nameRequired": "Un nom est obligatoire",
  "admin.plans.saveFailed": "La formule n’a pas pu être enregistrée",
  "admin.plans.name": "Nom de la formule *",
  "admin.plans.namePlaceholder": "p. ex. Professionnel",
  "admin.plans.description": "Description",
  "admin.plans.descriptionPlaceholder": "Description facultative",
  "admin.plans.limits": "Limites",
  "admin.plans.limitsHint": "(laisser vide pour illimité)",
  "admin.plans.maxSites": "Nombre maximal de sites",
  "admin.plans.maxUsers": "Nombre maximal d’utilisateurs",
  "admin.plans.durationDays": "Durée (jours)",
  "admin.plans.included": "Modules inclus",
  "admin.plans.selfSignup": "Disponible à l’inscription libre",
  "admin.plans.selfSignupHint": "Afficher cette formule sur la page d’inscription",
  "admin.plans.save": "Enregistrer la formule",
  "admin.admins.removed": "Administrateur global retiré",
  "admin.admins.removeFailed": "Échec du retrait",
  "admin.admins.reset2faDone": "Connexion à deux facteurs réinitialisée",
  "admin.admins.reset2faText": "La personne est déconnectée partout et la reconfigure à sa prochaine connexion.",
  "admin.admins.resetFailed": "Échec de la réinitialisation",
  "admin.admins.title": "Administrateurs globaux",
  "admin.admins.subtitle": "Personnes ayant les droits d’administrateur global : administration de la plateforme pour toutes les entreprises",
  "admin.admins.add": "Ajouter un administrateur global",
  "admin.admins.warning": "Les administrateurs globaux ont un accès complet à toutes les entreprises, tous les paramètres et toutes les données de la plateforme. Ne l’accordez qu’à du personnel de confiance.",
  "admin.admins.name": "Nom",
  "admin.admins.email": "E-mail",
  "admin.admins.role": "Rôle",
  "admin.admins.lastLogin": "Dernière connexion",
  "admin.admins.created": "Créé",
  "admin.admins.empty": "Aucun administrateur global trouvé",
  "admin.admins.you": "(vous)",
  "admin.admins.owner": "Propriétaire",
  "admin.admins.superAdmin": "Super-administrateur",
  "admin.admins.admin": "Administrateur",
  "admin.admins.invited": "Invité",
  "admin.admins.never": "Jamais",
  "admin.admins.edit": "Modifier",
  "admin.admins.editNamed": "Modifier {name}",
  "admin.admins.reset2fa": "Réinitialiser la connexion à deux facteurs",
  "admin.admins.reset2faFor": "Réinitialiser la connexion à deux facteurs de {name}",
  "admin.admins.reset2faConfirm": "Réinitialiser la connexion à deux facteurs de « {name} » ? La personne est déconnectée partout et la reconfigure à sa prochaine connexion.",
  "admin.admins.ownerFixed": "Le propriétaire de la plateforme ne peut pas être retiré",
  "admin.admins.notSelf": "Vous ne pouvez pas retirer vos propres droits",
  "admin.admins.remove": "Retirer",
  "admin.admins.removeNamed": "Retirer {name} des administrateurs globaux",
  "admin.admins.removeConfirm": "Retirer « {name} » ({email}) des administrateurs globaux ? Un compte rattaché à des entreprises les conserve ; un compte sans entreprise est supprimé.",
  "admin.admins.ownHint": "Votre nom, votre mot de passe et votre connexion à deux facteurs se trouvent dans",
  "admin.admins.invitationSent": "Invitation envoyée",
  "admin.admins.invitationText": "{email} définit un mot de passe depuis l’e-mail, puis la connexion à deux facteurs.",
  "admin.admins.added": "Administrateur global ajouté",
  "admin.admins.addedText": "Ce compte a désormais les droits d’administrateur global.",
  "admin.admins.editTitle": "Modifier : {name}",
  "admin.admins.updated": "Administrateur mis à jour",
  "admin.admins.required": "Un nom et une adresse e-mail sont obligatoires",
  "admin.admins.saveFailed": "Échec de l’enregistrement",
  "admin.admins.fullName": "Nom complet *",
  "admin.admins.emailAddress": "Adresse e-mail *",
  "admin.admins.emailAddressPlain": "Adresse e-mail",
  "admin.admins.emailHint": "Un compte existant reçoit les droits. Une nouvelle adresse reçoit un e-mail pour définir un mot de passe.",
  "admin.admins.emailFixed": "L’adresse e-mail ne peut pas être modifiée",
  "admin.admins.superAdminHint": "Peut gérer les autres administrateurs globaux et les paramètres critiques de la plateforme",
  "admin.admins.saveChanges": "Enregistrer les modifications",
  "admin.admins.sendInvitation": "Envoyer l’invitation",
  "admin.settings.provider": "Fournisseur",
  "admin.settings.serverSettings": "Utiliser les paramètres du serveur (.env)",
  "admin.settings.off": "Désactivé",
  "admin.settings.waHint": "Dès que Meta ou Twilio est choisi ici, seules les valeurs de cette page comptent : rien n’est repris des paramètres du serveur. Les modifications enregistrées s’appliquent immédiatement, sans redémarrage. Les jetons sont stockés chiffrés et ne sont plus jamais affichés. Les entreprises disposant du module de licence WhatsApp propre peuvent utiliser leur propre connexion (Paramètres de l’entreprise → WhatsApp).",
  "admin.settings.changedProvider": "Vous avez changé de fournisseur : enregistrez les paramètres pour l’utiliser.",
  "admin.settings.rcsHint": "Les messages RCS arrivent dans l’application de messages du téléphone, sous votre nom d’entreprise vérifié. Dès que Google ou Twilio est choisi ici, seules les valeurs de cette page comptent. Les modifications enregistrées s’appliquent immédiatement. Les clés et jetons sont stockés chiffrés et ne sont plus jamais affichés. Les entreprises disposant du module de licence RCS propre peuvent utiliser leur propre expéditeur (Paramètres de l’entreprise → RCS).",
  "admin.settings.templatesChecked": "Modèles vérifiés",
  "admin.settings.syncFailed": "Échec de la synchronisation",
  "admin.settings.tryAgain": "Réessayez dans un instant.",
  "admin.settings.templatesOff": "Lorsque WhatsApp passe par Twilio (choisi ci-dessus ou dans les paramètres du serveur), la plateforme crée elle-même les modèles et les entreprises peuvent modifier leurs textes. Avec Meta, les modèles indiqués dans ses paramètres sont utilisés.",
  "admin.settings.templatesIntro": "WhatsApp approuve chaque texte avant qu’il puisse être utilisé. Les nouveaux textes sont soumis et les verdicts récupérés toutes les 30 minutes ; tant que le texte propre d’une entreprise n’est pas approuvé, le texte par défaut est envoyé.",
  "admin.settings.templatesInUse": { one: "{count} modèle utilisé. WhatsApp en autorise {unverified} par compte professionnel, ou {verified} une fois l’entreprise vérifiée auprès de Meta. Le texte WhatsApp propre à chaque entreprise est un modèle à part entière.", other: "{count} modèles utilisés. WhatsApp en autorise {unverified} par compte professionnel, ou {verified} une fois l’entreprise vérifiée auprès de Meta. Le texte WhatsApp propre à chaque entreprise est un modèle à part entière." },
  "admin.settings.nearLimit": "Proche de la limite d’un compte professionnel non vérifié ({limit}). Faites vérifier l’entreprise par Meta, sinon les nouveaux textes ne pourront plus être approuvés une fois la limite atteinte.",
  "admin.settings.message": "Message",
  "admin.settings.text": "Texte",
  "admin.settings.usedBy": "Utilisé par",
  "admin.settings.authMessage": "Le message de code de vérification propre à WhatsApp",
  "admin.settings.usedBy.default": "Par défaut",
  "admin.settings.usedBy.previousDefault": "Ancien texte par défaut, jusqu’à l’approbation du nouveau",
  "admin.settings.notUsed": "Plus utilisé",
  "admin.settings.wa.approved": "approuvé",
  "admin.settings.wa.rejected": "refusé",
  "admin.settings.wa.failed": "non soumis",
  "admin.settings.wa.paused": "en pause",
  "admin.settings.wa.disabled": "désactivé",
  "admin.settings.wa.pending": "en attente",
  "admin.settings.noTemplates": "Aucun pour l’instant : la première synchronisation les crée.",
  "admin.settings.syncNow": "Synchroniser maintenant",
  "admin.settings.testSent": "E-mail de test envoyé",
  "admin.settings.checkInbox": "Consultez {to}",
  "admin.settings.smtpFailed": "Échec du test SMTP",
  "admin.settings.save": "Enregistrer les paramètres",
  "admin.settings.title": "Paramètres de la plateforme",
  "admin.settings.subtitle": "Comportement général, e-mail, WhatsApp, RCS et VPN",
  "admin.settings.tab": "Paramètres",
  "admin.settings.edgeTab": "Proxy d’entrée (Caddy + HAProxy) →",
  "admin.settings.loading": "Chargement",
  "admin.settings.behaviour": "Comportement de la plateforme",
  "admin.settings.licensing": "Licences activées",
  "admin.settings.licensingHint": "Appliquer les limites de licence. Désactiver pour un accès illimité.",
  "admin.settings.maintenance": "Mode maintenance",
  "admin.settings.maintenanceHint": "Empêche les utilisateurs des entreprises de se connecter. Les administrateurs globaux gardent l’accès.",
  "admin.settings.selfRegistration": "Autoriser l’inscription libre",
  "admin.settings.selfRegistrationHint": "Permet aux entreprises de s’inscrire sans invitation d’un administrateur global.",
  "admin.settings.adminIdle": "Administrateurs globaux : déconnexion après une période d’inactivité, au maximum",
  "admin.settings.adminIdleHint": "1 heure par défaut ; chaque administrateur peut choisir une durée plus courte dans Mon profil.",
  "admin.settings.idleOff": "Les administrateurs peuvent désactiver la déconnexion après inactivité",
  "admin.settings.idleOffHint": "Pour leur propre compte.",
  "admin.settings.idleOffWarning": "Cela permet aux administrateurs globaux de désactiver l’une des protections des comptes de la plateforme : une session d’administrateur laissée ouverte sur un ordinateur sans surveillance reste alors connectée jusqu’à sa durée maximale.",
  "admin.settings.idleOffQuestion": "L’autoriser ?",
  "admin.settings.defaultLicense": "Type de licence par défaut",
  "admin.settings.licenseType.TRIAL": "Essai",
  "admin.settings.licenseType.STANDARD": "Standard",
  "admin.settings.licenseType.UNLIMITED": "Illimitée",
  "admin.settings.licenseType.CUSTOM": "Personnalisée",
  "admin.settings.trialDays": "Jours d’essai par défaut",
  "admin.settings.email": "E-mail / SMTP",
  "admin.settings.emailIntro": "Valeurs par défaut pour toute la plateforme. Les entreprises peuvent utiliser leur propre SMTP dans leurs paramètres. Les mots de passe sont chiffrés en AES-256 avant stockage.",
  "admin.settings.smtpHost": "Hôte SMTP",
  "admin.settings.smtpPort": "Port SMTP",
  "admin.settings.username": "Nom d’utilisateur",
  "admin.settings.password": "Mot de passe",
  "admin.settings.saved": "✓ enregistré",
  "admin.settings.keepPassword": "(laisser vide pour conserver l’actuel)",
  "admin.settings.enterPassword": "Mot de passe SMTP",
  "admin.settings.hidePassword": "Masquer le mot de passe",
  "admin.settings.showPassword": "Afficher le mot de passe",
  "admin.settings.fromEmail": "Adresse d’expédition",
  "admin.settings.fromName": "Nom d’expéditeur",
  "admin.settings.sendTest": "Envoyer un e-mail de test",
  "admin.settings.testRecipient": "Destinataire du test",
  "admin.settings.testRecipientPlaceholder": "votre@e-mail.fr",
  "admin.settings.send": "Envoyer le test",
  "admin.settings.waTemplates": "Modèles WhatsApp",
  "admin.settings.vpn": "VPN / OpenVPN",
  "admin.settings.vpnIntro": "Détermine le VPN des nouvelles entreprises. Chaque entreprise reçoit son propre sous-réseau /24.",
  "admin.settings.serverHost": "Nom d’hôte / IP du serveur",
  "admin.settings.usersBase": "Base du réseau des utilisateurs",
  "admin.settings.sitesBase": "Base du réseau des sites",
  "admin.settings.usersExample": "Entreprise 1 → 10.8.1.0/24 · Entreprise 2 → 10.8.2.0/24 …",
  "admin.settings.sitesExample": "Entreprise 1 → 10.9.1.0/24 · Entreprise 2 → 10.9.2.0/24 …",
  "admin.licences.deleted": "Licence supprimée",
  "admin.licences.deleteFailed": "Échec de la suppression",
  "admin.licences.notified": "E-mail envoyé aux administrateurs de l’entreprise",
  "admin.licences.notifyFailed": "L’e-mail n’a pas pu être envoyé",
  "admin.licences.keyRevoked": "Clé révoquée",
  "admin.licences.revokeFailed": "Échec de la révocation",
  "admin.licences.unusedDeleted": "Clés inutilisées supprimées",
  "admin.licences.expiredDeleted": "Licences expirées supprimées",
  "admin.licences.bulkFailed": "Échec de la suppression",
  "admin.licences.tab.used": "Actives",
  "admin.licences.tab.available": "Clés",
  "admin.licences.tab.expired": "Expirées",
  "admin.licences.title": "Vue d’ensemble des licences",
  "admin.licences.subtitle": "Toutes les licences et clés émises sur la plateforme",
  "admin.licences.generate": "Générer une clé",
  "admin.licences.activeCount": "Licences actives",
  "admin.licences.availableCount": "Clés disponibles",
  "admin.licences.expiredCount": "Licences expirées",
  "admin.licences.totalKeys": "Clés émises au total",
  "admin.licences.company": "Entreprise",
  "admin.licences.plan": "Formule",
  "admin.licences.started": "Début",
  "admin.licences.expires": "Expire",
  "admin.licences.limits": "Limites",
  "admin.licences.noActive": "Aucune licence active",
  "admin.licences.daysLeft": { one: "{count} jour restant", other: "{count} jours restants" },
  "admin.licences.unlimited": "Illimitée",
  "admin.licences.sites": { one: "{count} site", other: "{count} sites" },
  "admin.licences.sitesUnlimited": "∞ sites",
  "admin.licences.users": { one: "{count} utilisateur", other: "{count} utilisateurs" },
  "admin.licences.usersUnlimited": "∞ utilisateurs",
  "admin.licences.thisCompany": "cette entreprise",
  "admin.licences.deleteFor": "Supprimer la licence de {name}",
  "admin.licences.deleteConfirm": "Supprimer la licence de « {name} » ?",
  "admin.licences.deleteUnusedConfirm": { one: "Supprimer la clé inutilisée ?", other: "Supprimer les {count} clés inutilisées ?" },
  "admin.licences.deleteUnused": "Supprimer toutes les inutilisées",
  "admin.licences.available": "Disponibles ({count})",
  "admin.licences.key": "Clé",
  "admin.licences.restriction": "Restriction",
  "admin.licences.created": "Créée",
  "admin.licences.redeemed": "Utilisées ({count})",
  "admin.licences.usedBy": "Utilisée par",
  "admin.licences.redeemedAt": "Utilisée le",
  "admin.licences.noKeys": "Aucune clé de licence pour l’instant — générez-en ci-dessus",
  "admin.licences.deleteExpiredConfirm": { one: "Supprimer la licence expirée ?", other: "Supprimer les {count} licences expirées ?" },
  "admin.licences.deleteExpired": "Supprimer toutes les expirées",
  "admin.licences.expiredOn": "Expirée le",
  "admin.licences.durationWas": "Durée",
  "admin.licences.noExpired": "Aucune licence expirée",
  "admin.licences.days": { one: "{count} jour", other: "{count} jours" },
  "admin.licences.notifyHint": "Prévenir par e-mail les administrateurs de l’entreprise de l’expiration",
  "admin.licences.notify": "Prévenir",
  "admin.licences.deleteExpiredFor": "Supprimer la licence expirée de {name}",
  "admin.licences.deleteExpiredOne": "Supprimer la licence expirée de « {name} » ?",
  "admin.licences.copyKey": "Copier la clé complète",
  "admin.licences.anyPlan": "Toutes",
  "admin.licences.companyOnly": "🏢 Une seule entreprise",
  "admin.licences.noRestriction": "🌐 Illimitée",
  "admin.licences.never": "Jamais",
  "admin.licences.revokeKey": "Révoquer la clé",
  "admin.licences.revokeConfirm": "Révoquer cette clé ?",
  "admin.licences.generated": { one: "{count} clé générée", other: "{count} clés générées" },
  "admin.licences.generateFailed": "Échec de la génération",
  "admin.licences.generateTitle": { one: "Générer une clé de licence", other: "Générer des clés de licence" },
  "admin.licences.sendKeys": "Copiez ces clés et envoyez-les à vos clients. Ils peuvent les utiliser à l’inscription ou dans les paramètres de leur compte.",
  "admin.licences.copy": "Copier",
  "admin.licences.allCopied": "Toutes les clés copiées",
  "admin.licences.copyAll": "Tout copier",
  "admin.licences.done": "Terminé",
  "admin.licences.licencePlan": "Formule de licence",
  "admin.licences.anyPlanOption": "Toute formule (sans restriction)",
  "admin.licences.restrict.unlimited": "🌐 Illimitée",
  "admin.licences.restrict.email": "📧 E-mail",
  "admin.licences.restrict.company": "🏢 Entreprise",
  "admin.licences.limitEmail": "Limiter à l’adresse e-mail",
  "admin.licences.limitCompany": "Limiter à l’entreprise",
  "admin.licences.chooseCompany": "Choisissez une entreprise…",
  "admin.licences.count": "Nombre de clés",
  "admin.licences.keyExpires": "Expiration de la clé",
  "admin.licences.optional": "(facultatif)",
  "admin.licences.notes": "Notes",
  "admin.licences.notesPlaceholder": "Note interne sur ce lot de clés",
  "admin.licences.generateCount": { one: "Générer la clé", other: "Générer {count} clés" },
  "admin.edge.resolves": "{domain} pointe correctement",
  "admin.edge.dnsNotHere": "Le DNS ne pointe pas encore ici",
  "admin.edge.resolvesTo": "Pointe vers {ips}",
  "admin.edge.noARecord": "Aucun enregistrement A trouvé",
  "admin.edge.proven": "Propriété prouvée",
  "admin.edge.servesNow": "Le domaine affiche désormais le tableau de bord.",
  "admin.edge.notProvenYet": "Pas encore prouvée",
  "admin.edge.markedProven": "Marquée comme prouvée",
  "admin.edge.ownershipReset": "Propriété réinitialisée",
  "admin.edge.resetText": "Le domaine affiche la page de configuration jusqu’à ce que le nouvel enregistrement TXT soit trouvé.",
  "admin.edge.failed": "Échec",
  "admin.edge.requestFailed": "La requête a échoué",
  "admin.edge.removed": "Domaine supprimé",
  "admin.edge.caddyApplied": "Configuration Caddy appliquée",
  "admin.edge.caddyRejected": "Caddy a refusé la configuration",
  "admin.edge.title": "Proxy de périphérie",
  "admin.edge.subtitle": "HAProxy devant :80/:443 · Caddy termine le TLS et émet les certificats à la demande · OpenVPN par entreprise en UDP",
  "admin.edge.addDomain": "Ajouter un domaine",
  "admin.edge.connections": { one: "{count} connexion", other: "{count} connexions" },
  "admin.edge.notReachable": "Injoignable",
  "admin.edge.onDemandOn": "TLS à la demande actif",
  "admin.edge.onDemandOff": "TLS à la demande NON configuré",
  "admin.edge.edgeIp": "IP de périphérie",
  "admin.edge.noEdgeIp": "Non défini — impossible d’indiquer aux clients où faire pointer leur DNS",
  "admin.edge.acme": "ACME : {email}",
  "admin.edge.noAcme": "Aucune adresse e-mail ACME définie",
  "admin.edge.caddyWarnBefore": "Caddy fonctionne, mais le TLS à la demande n’est pas relié au point de terminaison ask. Les domaines en marque blanche n’obtiendront pas de certificat tant que vous n’aurez pas appliqué la configuration — utilisez ",
  "admin.edge.caddyWarnAfter": " sous Configuration.",
  "admin.edge.tab.domains": "Domaines ({count})",
  "admin.edge.tab.vpn": "Instances VPN",
  "admin.edge.tab.config": "Configuration",
  "admin.edge.domain": "Domaine",
  "admin.edge.type": "Type",
  "admin.edge.company": "Entreprise",
  "admin.edge.dns": "DNS",
  "admin.edge.ownership": "Propriété",
  "admin.edge.certificate": "Certificat",
  "admin.edge.noDomains": "Aucun domaine pour l’instant",
  "admin.edge.typeName.WHITELABEL": "Marque blanche",
  "admin.edge.typeName.PLATFORM": "Plateforme",
  "admin.edge.typeName.APP": "Autre application",
  "admin.edge.typeName.REDIRECT": "Redirection",
  "admin.edge.typeName.VPN": "VPN",
  "admin.edge.typeDesc.WHITELABEL": "Le domaine du tableau de bord propre à un client",
  "admin.edge.typeDesc.PLATFORM": "Votre propre nom d’hôte pour le tableau de bord",
  "admin.edge.typeDesc.APP": "Une autre application web sur ce serveur",
  "admin.edge.typeDesc.REDIRECT": "Renvoie ce nom vers un autre",
  "admin.edge.dnsResolves": "pointe",
  "admin.edge.awaitingDns": "en attente du DNS",
  "admin.edge.provenBadge": "prouvée",
  "admin.edge.provenBy": "par {name}",
  "admin.edge.anAdmin": "un administrateur",
  "admin.edge.byDns": "par enregistrement DNS",
  "admin.edge.notProven": "non prouvée",
  "admin.edge.certFailed": "échec",
  "admin.edge.certOnFirst": "émis à la première requête",
  "admin.edge.certAuto": "demandé automatiquement",
  "admin.edge.pendingDns": "en attente du DNS",
  "admin.edge.recheck": "Vérifier à nouveau le DNS",
  "admin.edge.resetOwnership": "Réinitialiser la propriété",
  "admin.edge.resetConfirm": "Réinitialiser la propriété de {domain} ? Il affiche la page « en cours de configuration » jusqu’à ce que l’entreprise publie le nouvel enregistrement TXT.",
  "admin.edge.remove": "Supprimer",
  "admin.edge.removeWhitelabel": "Supprimer {domain} ? Ses configurations VPN reviennent au nom d’hôte de la plateforme.",
  "admin.edge.removeOther": "Supprimer {domain} ? Ce serveur cessera d’y répondre.",
  "admin.edge.txtRecord": "Enregistrement de propriété (TXT) :",
  "admin.edge.copyRecord": "Copier la valeur de l’enregistrement pour {domain}",
  "admin.edge.releasedOn": "Libéré le {date} sauf preuve",
  "admin.edge.checkNow": "Vérifier maintenant",
  "admin.edge.overrideConfirm": "Marquer {domain} comme prouvée sans son enregistrement TXT ? Il affichera immédiatement le tableau de bord de {company}.",
  "admin.edge.theCompany": "l’entreprise",
  "admin.edge.markProven": "Marquer comme prouvée",
  "admin.edge.vpnIntroBefore": "Deux serveurs OpenVPN par entreprise (utilisateurs et sites), chacun sur son propre port UDP. Un redémarrage ou une mauvaise configuration ne concerne que cette entreprise. Les entreprises en marque blanche ont besoin d’un enregistrement A ",
  "admin.edge.vpnIntroAfter": " pointant vers l’IP de périphérie.",
  "admin.edge.theirDomain": "leur-domaine",
  "admin.edge.unit": "Unité",
  "admin.edge.udpPorts": "Ports UDP",
  "admin.edge.connectTo": "Les clients se connectent à",
  "admin.edge.state": "État",
  "admin.edge.noInstances": "Aucune instance VPN provisionnée",
  "admin.edge.whitelabelHost": "marque blanche : {domain}",
  "admin.edge.usersPort": "utilisateurs {port}",
  "admin.edge.sitesPort": "sites {port}",
  "admin.edge.repoint": "Repointer vers le domaine actuel",
  "admin.edge.hostRefreshed": "Hôte distant actualisé",
  "admin.edge.reprovision": "Provisionner à nouveau",
  "admin.edge.vpnRunning": "Serveurs VPN en marche",
  "admin.edge.queued": { one: "{count} compte qui n’avait pas pu être créé est en file d’attente", other: "{count} comptes qui n’avaient pas pu être créés sont en file d’attente" },
  "admin.edge.vpnNotRunning": "Serveurs VPN arrêtés",
  "admin.edge.provisionFailed": "Échec du provisionnement",
  "admin.edge.stop": "Arrêter",
  "admin.edge.stopConfirm": "Arrêter le VPN de {company} ? Ses tunnels seront coupés.",
  "admin.edge.attention": "Requiert votre attention",
  "admin.edge.healthy": "Opérationnel",
  "admin.edge.unreachable": "Injoignable",
  "admin.edge.caddyText": "Envoie la configuration générée par l’API d’administration. Les domaines de la plateforme y sont listés explicitement ; les domaines en marque blanche utilisent le TLS à la demande contrôlé par le point de terminaison ask, donc en ajouter un ne nécessite aucun rechargement.",
  "admin.edge.applyCaddy": "Appliquer la configuration Caddy",
  "admin.edge.haproxyText1": "Mis en place par l’installateur et modifié uniquement lorsque la périphérie elle-même change. La configuration attendue figure ci-dessous : pour l’appliquer, copiez-la dans ",
  "admin.edge.haproxyText2": " sur le serveur, vérifiez-la avec ",
  "admin.edge.haproxyText3": ", remplacez ",
  "admin.edge.haproxyText4": " par celle-ci et exécutez ",
  "admin.edge.haproxyText5": ". Le tableau de bord ne l’écrit pas : l’API s’exécute sans root.",
  "admin.edge.generatedCfg": "haproxy.cfg générée",
  "admin.edge.copy": "Copier",
  "admin.edge.registered": "Domaine enregistré",
  "admin.edge.addFailed": "Le domaine n’a pas pu être ajouté",
  "admin.edge.registeredTitle": "{domain} enregistré",
  "admin.edge.currentlyResolves": "Pointe actuellement vers : {ips}",
  "admin.edge.done": "Terminé",
  "admin.edge.domainLabel": "Domaine *",
  "admin.edge.pointA": "Faites pointer un enregistrement A vers ",
  "admin.edge.certSoon": " Le certificat est demandé dès que le nom pointe ici.",
  "admin.edge.certFirst": " Le certificat est émis automatiquement à la première requête.",
  "admin.edge.pointASuffix": ".",
  "admin.edge.portLabel": "Port sur ce serveur *",
  "admin.edge.portHint": "https://{domain} mène à http://127.0.0.1:{port}, sans rien du tableau de bord entre les deux. Les ports utilisés par HAProxy, Caddy ou le tableau de bord sont refusés.",
  "admin.edge.redirectLabel": "Rediriger vers *",
  "admin.edge.redirectHint": "Un nom d’hôte. Chaque requête y est envoyée en HTTPS avec son propre chemin et ses paramètres, en redirection permanente.",
  "admin.edge.companyLabel": "Entreprise *",
  "admin.edge.chooseCompany": "Choisissez une entreprise…",
  "admin.edge.vpnSwitchBefore": "Ses configurations VPN passent automatiquement à ",
  "admin.edge.vpnSwitchAfter": ".",
  "admin.edge.step.prove": "Prouvez que vous possédez {domain} : ajoutez un enregistrement TXT nommé {name} avec la valeur {value}. D’ici là, {domain} affiche une page « en cours de configuration ».",
  "admin.edge.step.pointAt": "Faites pointer un enregistrement A pour {domain} vers {ip}, puis vérifiez à nouveau.",
  "admin.edge.step.pointHere": "Faites pointer un enregistrement A pour {domain} vers ce serveur, puis vérifiez à nouveau. Définissez l’IP publique de périphérie dans les paramètres de la plateforme pour une vérification automatique.",
  "admin.edge.step.appOk": "Le DNS est correct : https://{domain} mène désormais à l’application sur le port {port} de ce serveur, et son certificat est en cours de demande.",
  "admin.edge.step.redirectOk": "Le DNS est correct : https://{domain} redirige désormais vers https://{target}, et son certificat est en cours de demande.",
  "admin.edge.step.dnsOk": "Le DNS est correct. Caddy émet un certificat à la première requête vers https://{domain}.",
  "admin.edge.step.vpnRecord": "Créez un enregistrement A pour {host} pointant vers {ip} — les clients VPN s’y connectent.",
  "admin.edge.thisServer": "ce serveur",
  "admin.edge.step.vpnHost": "Les configurations VPN de cette entreprise utilisent désormais {host}. Les fichiers .ovpn existants continuent de fonctionner tant que leur ancien nom d’hôte pointe ici ; téléchargez-les à nouveau pour basculer.",
  "admin.companies.title": "Entreprises",
  "admin.companies.count": { one: "{count} entreprise sur la plateforme", other: "{count} entreprises sur la plateforme" },
  "admin.companies.create": "Créer une entreprise",
  "admin.companies.search": "Rechercher des entreprises",
  "admin.companies.searchPlaceholder": "Rechercher par nom ou slug",
  "admin.companies.company": "Entreprise",
  "admin.companies.status": "Statut",
  "admin.companies.licence": "Licence",
  "admin.companies.sites": "Sites",
  "admin.companies.users": "Utilisateurs",
  "admin.companies.created": "Créée",
  "admin.companies.expiresOn": "Expire le {date}",
  "admin.companies.edit": "Modifier l’entreprise",
  "admin.companies.settings": "Paramètres de l’entreprise",
  "admin.companies.usersInvites": "Utilisateurs et invitations",
  "admin.companies.manageLicence": "Gérer la licence",
  "admin.companies.assignAdmin": "Désigner un administrateur",
  "admin.companies.delete": "Supprimer l’entreprise",
  "admin.companies.createFailed": "L’entreprise n’a pas pu être créée",
  "admin.companies.nameLabel": "Nom de l’entreprise *",
  "admin.companies.slugLabel": "Slug *",
  "admin.companies.adminUser": "Administrateur",
  "admin.companies.adminName": "Nom de l’administrateur *",
  "admin.companies.adminEmail": "Adresse e-mail de l’administrateur *",
  "admin.companies.licenceTypeLabel": "Type de licence",
  "admin.companies.trial30": "Essai (30 jours)",
  "admin.companies.expiryDate": "Date d’expiration",
  "admin.companies.maxSites": "Sites max.",
  "admin.companies.maxUsers": "Utilisateurs max.",
  "admin.companies.unlimited": "Illimité",
  "admin.companies.licenceUpdated": "Licence mise à jour",
  "admin.companies.saveFailed": "Échec de l’enregistrement",
  "admin.companies.assignLicence": "Attribuer une licence",
  "admin.companies.fromPlan": "📋 Depuis une formule",
  "admin.companies.manual": "✏️ Manuellement",
  "admin.companies.licencePlan": "Formule de licence",
  "admin.companies.noPlansBefore": "Aucune formule de licence active — créez-en d’abord une sous ",
  "admin.companies.noPlansLink": "Formules de licence",
  "admin.companies.noPlansAfter": ".",
  "admin.companies.blankUnlimited": "(vide = illimitée)",
  "admin.companies.modules": "Modules",
  "admin.companies.modulesHint": "Définis à la main, ils remplacent ceux d’une formule : l’enregistrement détache la licence de sa formule. Choisir une formule plus tard rétablit ses modules.",
  "admin.companies.notes": "Notes",
  "admin.companies.internalNote": "Note interne",
  "admin.companies.inviteSent": "Invitation envoyée",
  "admin.companies.inviteResent": "L’invitation a été renvoyée à l’utilisateur.",
  "admin.companies.resendFailed": "L’invitation n’a pas pu être renvoyée",
  "admin.companies.twoFactorReset": "Connexion à deux facteurs réinitialisée",
  "admin.companies.twoFactorResetText": "La personne est déconnectée partout et la configure à nouveau à sa prochaine connexion.",
  "admin.companies.resetFailed": "Échec de la réinitialisation",
  "admin.companies.usersTitle": "Utilisateurs de l’entreprise",
  "admin.companies.noUsers": "Aucun utilisateur dans cette entreprise",
  "admin.companies.twoFactorOn": "La connexion à deux facteurs est activée",
  "admin.companies.resendInvite": "Renvoyer l’invitation",
  "admin.companies.resend": "Renvoyer",
  "admin.companies.reset2fa": "Réinitialiser la connexion à deux facteurs",
  "admin.companies.reset2faFor": "Réinitialiser la connexion à deux facteurs de {name}",
  "admin.companies.reset2faConfirm": "Réinitialiser la connexion à deux facteurs de « {name} » ? La personne est déconnectée partout et la configure à nouveau à sa prochaine connexion.",
  "admin.companies.updateFailed": "L’entreprise n’a pas pu être mise à jour",
  "admin.companies.domainPlaceholder": "p. ex. portal.acme.com",
  "admin.companies.statusName.PENDING_SETUP": "En configuration",
  "admin.companies.statusName.ACTIVE": "Active",
  "admin.companies.statusName.SUSPENDED": "Suspendue",
  "admin.companies.statusName.CANCELLED": "Résiliée",
  "admin.companies.saveChanges": "Enregistrer les modifications",
  "admin.companies.notSaved": "Paramètres non enregistrés",
  "admin.companies.tryAgain": "Réessayez dans un instant.",
  "admin.companies.monitoring": "Surveillance",
  "admin.companies.timezone": "Fuseau horaire",
  "admin.companies.language": "Langue",
  "admin.companies.ownMail": "Serveur de messagerie propre (au lieu de celui de la plateforme)",
  "admin.companies.mailInUse": "Utilisé : le serveur de messagerie propre à l’entreprise.",
  "admin.companies.mailNotYet": "Pas encore utilisé : tant que l’administrateur de l’entreprise n’a pas envoyé d’e-mail de test depuis les Paramètres de l’entreprise, ses e-mails passent par le serveur de la plateforme.",
  "admin.companies.port": "Port",
  "admin.companies.passwordSaved": "✓ enregistré",
  "admin.companies.deleteFailed": "Échec de la suppression",
  "admin.companies.couldNotDelete": "L’entreprise n’a pas pu être supprimée",
  "admin.companies.permanent": "Cette action est définitive.",
  "admin.companies.deleteTextBefore": " Tous les sites, utilisateurs, modèles, données de surveillance, configurations VPN et paramètres de ",
  "admin.companies.deleteTextAfter": " sont supprimés immédiatement et ne peuvent pas être récupérés.",
  "admin.companies.typeBefore": "Saisissez ",
  "admin.companies.typeAfter": " pour confirmer",
  "admin.companies.deletePermanently": "Supprimer définitivement",
  "admin.companies.userInvited": "Utilisateur invité",
  "admin.companies.userUpdated": "Utilisateur mis à jour",
  "admin.companies.invitedText": "Une invitation a été envoyée à {email}. La personne définit son mot de passe via cet e-mail.",
  "admin.companies.nowAdmin": "{email} est désormais administrateur de cette entreprise.",
  "admin.companies.nowMember": "{email} est désormais membre de cette entreprise.",
  "admin.companies.assignFailed": "L’utilisateur n’a pas pu être ajouté",
  "admin.companies.assignTitle": "Ajouter ou inviter un utilisateur",
  "admin.companies.assignIntro": "Saisissez une adresse e-mail pour inviter quelqu’un dans cette entreprise. Une personne qui a déjà un compte Velanox est ajoutée immédiatement ; les autres reçoivent une invitation pour définir un mot de passe.",
  "admin.companies.emailLabel": "Adresse e-mail *",
  "admin.companies.nameOnlyNew": "(uniquement pour les nouveaux comptes)",
  "admin.companies.makeAdmin": "Nommer administrateur de l’entreprise",
  "admin.companies.makeAdminHint": "Activé : accès administrateur complet. Désactivé : ajouté comme simple membre.",
  "admin.companies.assignAsAdmin": "Ajouter comme administrateur",
  "admin.companies.inviteAsMember": "Inviter comme membre",
  "admin.module.rental.label": "Location",
  "admin.module.rental.desc": "Stock, planification, événements et achats pour la location de matériel",
  "admin.edge.mikrotikPort": "MikroTik {port}",
  "admin.edge.vpnRunningNoMikrotik": "Serveurs VPN en marche — le serveur MikroTik n’a pas démarré",
  "admin.module.omada.label": "Omada",
  "admin.module.omada.desc": "Contrôleurs TP-Link Omada : leurs sites, points d’accès, switches et passerelles",
  "admin.module.ruckus.label": "Ruckus",
  "admin.module.ruckus.desc": "Contrôleurs Ruckus SmartZone : leurs zones, points d’accès et clients",
  "admin.module.starlink.label": "Starlink",
  "admin.module.starlink.desc": "Un compte professionnel Starlink : ses terminaux, leurs données et leur service",
  "admin.models.title": "Modèles",
  "admin.models.subtitle": "Chaque modèle d'appareil que le parc signale, les nouveaux, et ce que Velanox ne lit qu'en partie",
  "admin.models.inUse": "En service",
  "admin.models.gone": "Disparus",
  "admin.models.brand": "Marque",
  "admin.models.allBrands": "Toutes les marques",
  "admin.models.col.model": "Modèle",
  "admin.models.col.devices": "Appareils",
  "admin.models.col.companies": "Entreprises",
  "admin.models.col.firmware": "Micrologiciel",
  "admin.models.col.firstSeen": "Vu pour la première fois",
  "admin.models.col.lastSeen": "Vu pour la dernière fois",
  "admin.models.col.partly": "Lu en partie",
  "admin.models.new": "Nouveau",
  "admin.models.partly": "Lu en partie ({n})",
  "admin.models.times": "rencontré {times}×",
  "admin.models.noExample": "Site d'exemple supprimé",
  "admin.models.example": "{site} ({company})",
  "admin.models.none": "Aucun modèle vu pour l'instant",
  "admin.models.noneGone": "Aucun modèle disparu",
  "admin.models.kind.interface_type": "Type d'interface",
  "admin.models.kind.part_not_answering": "Partie sans réponse",
  "admin.models.kind.device_type": "Type d'appareil",
  "admin.models.kind.wan_type": "Type de WAN",
  "admin.models.kind.dish_alert": "Champ d'alerte de l'antenne",
  "admin.settings.push.title": "Notifications push",
  "admin.settings.push.intro": "Les notifications push vers l'application Velanox passent par Firebase. Avec sa clé, cette installation les envoie elle-même ; sans, par le relais Velanox.",
  "admin.settings.push.direct": "Envoyées directement par Firebase avec la clé de cette installation",
  "admin.settings.push.off": "Désactivées : pas de clé Firebase et le relais est coupé (PUSH_RELAY_URL)",
  "admin.settings.push.viaRelay": "Par le relais Velanox",
  "admin.settings.push.relayState.not_signed_up": "s'inscrit à la première notification",
  "admin.settings.push.relayState.signed_up": "inscrite",
  "admin.settings.push.relayState.blocked": "bloquée par le relais",
  "admin.settings.push.key": "Clé Firebase",
  "admin.settings.push.project": "Projet {project}",
  "admin.settings.push.upload": "Téléverser la clé Firebase",
  "admin.settings.push.keyFile": "Fichier de clé Firebase",
  "admin.settings.push.uploadHint": "La clé JSON du compte de service du projet Firebase de l'application Velanox (docs/mobile/stores.md). Uniquement sur l'installation qui relaie.",
  "admin.settings.push.remove": "Supprimer la clé Firebase",
  "admin.settings.push.removeConfirm": "Supprimer la clé Firebase ? Les notifications passent alors par le relais Velanox et les autres installations ne peuvent plus envoyer par celle-ci.",
  "admin.settings.push.relayOn": "Laisser les autres installations envoyer par celle-ci",
  "admin.settings.push.relayOnHint": "Elles s'inscrivent elles-mêmes ; chacune a une limite horaire. Voir sous Relais push.",
  "admin.settings.push.test": "Envoyer un test",
  "admin.settings.push.testSent": "Envoyé à {n} téléphone(s)",
  "admin.settings.push.testFailed": "La notification de test n'est pas partie",
  "admin.pushRelay.title": "Relais push",
  "admin.pushRelay.subtitle": "Les installations qui envoient leurs notifications par celle-ci : comptées, jamais leurs textes",
  "admin.pushRelay.col.installation": "Installation",
  "admin.pushRelay.col.signedUp": "Inscrite",
  "admin.pushRelay.col.lastPush": "Dernière notification",
  "admin.pushRelay.col.today": "Aujourd'hui",
  "admin.pushRelay.col.month": "Ce mois-ci",
  "admin.pushRelay.col.limit": "Par heure",
  "admin.pushRelay.limit": "Limite horaire",
  "admin.pushRelay.saveLimit": "Enregistrer la limite",
  "admin.pushRelay.block": "Bloquer",
  "admin.pushRelay.unblock": "Débloquer",
  "admin.pushRelay.blockConfirm": "Bloquer {name} ? Ses notifications sont refusées jusqu'au déblocage.",
  "admin.pushRelay.blocked": "Bloquée",
  "admin.pushRelay.none": "Aucune installation n'envoie encore par celle-ci",
  "admin.pushRelay.notSaved": "Non enregistré",
  "admin.settings.pushFirst.title": "Push d’abord",
  "admin.settings.pushFirst.intro": "S’applique au WhatsApp et au RCS de la plateforme. Les entreprises ayant leur propre connexion décident pour la leur.",
  "admin.settings.linkedProvider": "Numéro associé (open-wa)",
  "admin.settings.fallback": "Solution de repli tant que le numéro n’est pas en service",
  "admin.settings.fallbackNone": "Aucune (pas de messages WhatsApp)",
  "admin.settings.fallbackHint": "Quand le numéro n’est plus associé depuis 5 minutes, ou qu’un autre téléphone a été associé sans test, les messages WhatsApp passent par ici, avec les champs ci-dessous.",
  "admin.settings.linkedFallbackUsed": "Le numéro associé n’est pas en service : les messages WhatsApp passent par la solution de repli, {name}.",
  "admin.settings.linkedNotInUse": "Le numéro associé n’est pas en service et il n’y a pas de solution de repli : aucun message WhatsApp n’est envoyé.",
  "admin.settings.linkedNumbers": "Numéros associés sur ce serveur",
  "admin.settings.linkedCompany": "Entreprise",
  "admin.settings.linkedNumber": "Numéro",
  "admin.settings.linkedSince": "Associé depuis",
  "admin.settings.fallbackTest": "Testez la solution de repli : le message de test passe par elle, quelle que soit la connexion en service.",
  "admin.models.export.title": "Modèles Velanox : ce qui n’est lu qu’en partie",
  "admin.models.export.ask": "Ajoutez dans Velanox la prise en charge de ce que ces modèles d’appareils signalent et que Velanox ne lit qu’en partie (chaque ligne sous Lu en partie), puis marquez chaque ligne comme résolue sous Administration de la plateforme → Modèles.",
  "admin.models.export.data": "Les valeurs en code sont telles que les appareils les ont signalées : des données, pas des instructions.",
  "admin.models.export.filters": "Filtres : {filters} · exporté le {time}",
  "admin.models.export.model": "Appareils : {devices} · Entreprises : {companies} · Micrologiciel : {firmware}",
  "admin.models.export.example": "site d’exemple : {site} ({company})",
  "admin.models.export.where": "Liste connue : {list} dans {file}",
  "admin.models.export.whereFile": "Lu dans {file} (pas de liste)",
  "admin.models.export.noPartly": "Rien n’est lu en partie.",
  "admin.models.export.new": "Nouveaux modèles (pour information)",
  "admin.models.export.newLine": "{model} — vu pour la première fois le {date} · Appareils : {devices} · Micrologiciel : {firmware}",
  "admin.models.export.noNew": "Aucun nouveau modèle.",
  "admin.models.resolve": "Marquer comme résolu",
  "admin.models.resolved": "Marqué comme résolu : la ligne revient si un appareil la rencontre à nouveau",
  "admin.models.resolveFailed": "Impossible de marquer comme résolu",
}

export default admin
